import React, { useCallback, useEffect, useRef, useState } from "react";
export interface DraggablePosition {
  x: number;
  y: number;
}
export interface UseDraggableOptions {
  /** Initial position. If not provided, element will start at default CSS position */
  initialPosition?: DraggablePosition;
  /** Padding from viewport edges in pixels */
  viewportPadding?: number;
  /** Whether dragging is currently disabled */
  disabled?: boolean;
  /** Callback when position changes */
  onPositionChange?: (position: DraggablePosition) => void;
}
export interface UseDraggableReturn<T extends HTMLElement = HTMLElement> {
  /** Current position of the draggable element */
  position: DraggablePosition | undefined;
  /** Callback ref to attach to the draggable container */
  containerRef: (el: T | null) => void;
  /** Handler for pointer down event */
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  /** Handler for pointer move event */
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  /** Handler for pointer up event */
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  /** Handler for pointer cancel event */
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
  /** Whether the element is currently being dragged */
  isDragging: boolean;
  /** Programmatically set position */
  setPosition: React.Dispatch<React.SetStateAction<DraggablePosition | undefined>>;
}

/**
 * Hook that provides draggable functionality with viewport bounds
 *
 * @example
 * ```tsx
 * const { containerRef, position, onPointerDown, onPointerMove, onPointerUp, onPointerCancel } = useDraggable({
 *   viewportPadding: 8,
 * });
 *
 * return (
 *   <div
 *     ref={containerRef}
 *     style={{
 *       position: 'fixed',
 *       left: position ? `${position.x}px` : undefined,
 *       top: position ? `${position.y}px` : undefined,
 *     }}
 *     onPointerDown={onPointerDown}
 *     onPointerMove={onPointerMove}
 *     onPointerUp={onPointerUp}
 *     onPointerCancel={onPointerCancel}
 *   >
 *     Drag me!
 *   </div>
 * );
 * ```
 */
export function useDraggable<T extends HTMLElement = HTMLDivElement>(options: UseDraggableOptions = {}): UseDraggableReturn<T> {
  const {
    initialPosition,
    viewportPadding = 8,
    disabled = false,
    onPositionChange
  } = options;
  const containerRef = useRef<T | null>(null);
  // Mirrors containerRef so effects re-run when the element mounts later
  // (e.g. a dialog whose content only renders once it opens).
  const [element, setElement] = useState<T | null>(null);

  // Callback ref that sets touch-action: none when the element mounts.
  // This ensures the style is applied even after conditional re-renders
  // (e.g., recording-pip.tsx returns null when not visible).
  const callbackRef = useCallback((el: T | null) => {
    containerRef.current = el;
    setElement(el);
    if (el && !disabled) {
      el.style.touchAction = "none";
    }
  }, [disabled]);
  const [position, setPositionState] = useState<DraggablePosition | undefined>(initialPosition);
  const dragStateRef = useRef<{
    active: boolean;
    dx: number;
    dy: number;
  }>({
    active: false,
    dx: 0,
    dy: 0
  });
  const setPosition: React.Dispatch<React.SetStateAction<DraggablePosition | undefined>> = posOrUpdater => {
    setPositionState(prev => {
      const newPos = typeof posOrUpdater === "function" ? posOrUpdater(prev) : posOrUpdater;
      if (newPos && onPositionChange) {
        onPositionChange(newPos);
      }
      return newPos;
    });
  };

  // Clamp position to viewport bounds
  const clampPosition = (x: number, y: number, rect: DOMRect): DraggablePosition => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const minX = viewportPadding;
    const minY = viewportPadding;
    const maxX = Math.max(minX, vw - rect.width - viewportPadding);
    const maxY = Math.max(minY, vh - rect.height - viewportPadding);
    return {
      x: Math.min(Math.max(minX, x), maxX),
      y: Math.min(Math.max(minY, y), maxY)
    };
  };

  // Keep inside viewport on window resize
  useEffect(() => {
    const onResize = () => {
      if (!position || !containerRef.current) return;
      const rect_0 = containerRef.current.getBoundingClientRect();
      const clamped = clampPosition(position.x, position.y, rect_0);
      if (clamped.x !== position.x || clamped.y !== position.y) {
        setPosition(clamped);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [position, viewportPadding]);

  // Clamp when the element's size changes
  useEffect(() => {
    const el_0 = element;
    if (!el_0) return;
    const ro = new ResizeObserver(() => {
      setPosition(prev_0 => {
        if (!prev_0) return prev_0;
        const rect_1 = el_0.getBoundingClientRect();
        const clamped_0 = clampPosition(prev_0.x, prev_0.y, rect_1);
        if (clamped_0.x !== prev_0.x || clamped_0.y !== prev_0.y) {
          return clamped_0;
        }
        return prev_0;
      });
    });
    ro.observe(el_0);
    return () => ro.disconnect();
  }, [element, viewportPadding]);
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (disabled) return;

    // Avoid dragging from interactive elements.
    //
    // Radix menus / selects / popovers portal their content to document.body,
    // but React still bubbles their synthetic pointer events up the *React*
    // tree — through this draggable container. Their items (role=menuitem,
    // role=option, …) aren't plain buttons, so without these selectors the
    // container would treat a menu/option press as a drag start, preventDefault
    // the pointerdown, and swallow the click. `[data-radix-popper-content-wrapper]`
    // catches all Radix floating content regardless of role.
    //
    // A match that contains the container is the container itself (or one of
    // its ancestors), not an interactive child -- e.g. a non-modal Radix dialog
    // made draggable carries role=dialog on its own root -- so it still drags.
    const el_1 = containerRef.current;
    if (!el_1) return;
    const target = e.target as HTMLElement;
    const interactive = target.closest("button,[role=button],select,[role=combobox],input,textarea,[data-no-drag]," + "[data-radix-popper-content-wrapper],[role=menu],[role=menuitem]," + "[role=menuitemradio],[role=menuitemcheckbox],[role=listbox],[role=option],[role=dialog]");
    if (interactive && !interactive.contains(el_1)) {
      return;
    }
    const rect_2 = el_1.getBoundingClientRect();
    const currentX = position?.x ?? rect_2.left;
    const currentY = position?.y ?? rect_2.top;
    setPosition({
      x: currentX,
      y: currentY
    });
    e.preventDefault();
    dragStateRef.current.active = true;
    dragStateRef.current.dx = e.clientX - currentX;
    dragStateRef.current.dy = e.clientY - currentY;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (error) {
      console.error("Failed to set pointer capture", error);
    }
  };
  const onPointerMove = (e_0: React.PointerEvent<HTMLElement>) => {
    if (disabled) return;
    if (!dragStateRef.current.active || !containerRef.current) return;
    // Only suppress default once a drag is actually in progress — otherwise this
    // fires for pointer events bubbling from portaled menu/select content (React
    // bubbles synthetic events through portals) and would interfere with them.
    e_0.preventDefault();
    const rect_3 = containerRef.current.getBoundingClientRect();
    const newX = e_0.clientX - dragStateRef.current.dx;
    const newY = e_0.clientY - dragStateRef.current.dy;
    const clamped_1 = clampPosition(newX, newY, rect_3);
    setPosition(clamped_1);
  };
  const onPointerUp = (e_1: React.PointerEvent<HTMLElement>) => {
    if (!dragStateRef.current.active) return;
    dragStateRef.current.active = false;
    if (containerRef.current) {
      try {
        e_1.currentTarget.releasePointerCapture?.(e_1.pointerId);
      } catch (error_0) {
        console.error("Failed to release pointer capture", error_0);
      }
    }
  };
  const onPointerCancel = onPointerUp;
  return {
    position,
    containerRef: callbackRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    isDragging: dragStateRef.current.active,
    setPosition
  };
}